import { OrrekieShell, PageContainer, LoadingState } from '@orrekie/ui';

import { AuthProvider } from '@orrekie/shared';
import { Suspense, lazy } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import ErrorBoundary from './components/ErrorBoundary.tsx';
import { UserShellTopbar } from './components/UserShellTopbar';
import { WorkspaceProviders } from './context/WorkspaceContext';
import { useEnforceLightThemeForFreeAudience } from './hooks/useEnforceLightThemeForFreeAudience';
import { useAppShellAudience, useUserShellSidebar } from './hooks/useUserShellSidebar';

const AuthDebugOverlay = import.meta.env.DEV ? lazy(() => import('./components/dev/AuthDebugOverlay')) : null;
const Loader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingState label="Loading app…" />
  </div>
);

function AppShellInner() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shellAudience = useAppShellAudience();
  const sidebarSections = useUserShellSidebar(shellAudience);
  useEnforceLightThemeForFreeAudience(shellAudience === 'free');

  return (
    <>
      <OrrekieShell
        sidebarSections={sidebarSections}
        currentPath={pathname}
        onNavigate={navigate}
        utilityHeaderRight={<UserShellTopbar audience={shellAudience} />}
        showThemeToggle={shellAudience !== 'free'}
      >
        <PageContainer>
          <Suspense fallback={<Loader />}>
            <Outlet />
          </Suspense>
        </PageContainer>
      </OrrekieShell>
      <Toaster richColors position="top-right" />
      {import.meta.env.DEV && import.meta.env.VITE_AUTH_DEBUG === 'true' && AuthDebugOverlay && (
        <Suspense fallback={null}>
          <AuthDebugOverlay />
        </Suspense>
      )}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <WorkspaceProviders>
          <AppShellInner />
        </WorkspaceProviders>
      </AuthProvider>
    </ErrorBoundary>
  );
}
